/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, User, Building2, Store, Check, AlertCircle, CheckCircle2, Mail, Lock, UserCircle, Home, Clock, MapPin, Camera, RotateCcw, Globe, Phone, FileText, Zap } from 'lucide-react';
import { COUNTRIES } from '../utils/regions';
import { FACILITIES_LIST } from '../utils/facilities';
import { FacilityIcon } from '../components/FacilityDisplay';
import { OpeningHoursEditor } from '../components/OpeningHoursEditor';
import AlternativeOpeningEditor from '../components/AlternativeOpeningEditor';
import SpecialOpeningEditor from '../components/SpecialOpeningEditor';
import AddressForm from '../components/AddressForm';
import { countryToFlag, EUROPEAN_COUNTRIES } from '../utils/countryFlags';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Live registration completeness calculator
const useRegistrationProgress = (userType, fields) => {
  return useMemo(() => {
    if (userType === 'offer') {
      const checks = [
        { key: 'companyName', label: 'Firmanavn', done: !!fields.companyName?.trim() },
        { key: 'contactPerson', label: 'Kontaktperson', done: !!fields.contactPerson?.trim() },
        { key: 'email', label: 'Email', done: !!fields.email?.trim() && fields.email.includes('@') },
        { key: 'phone', label: 'Telefon', done: !!fields.phone?.trim() },
        { key: 'description', label: 'Beskrivelse', done: !!fields.offerDescription?.trim() },
        { key: 'password', label: 'Adgangskode', done: (fields.password?.length || 0) >= 6 },
      ];
      const done = checks.filter(c => c.done).length;
      return { percent: Math.round((done / checks.length) * 100), checks };
    }
    if (userType === 'club') {
      const hasHours = fields.openingHours && Object.values(fields.openingHours).some(v => v?.trim());
      const checks = [
        { key: 'name', label: 'Dit navn', done: !!fields.name?.trim() },
        { key: 'clubName', label: 'Klubnavn', done: !!fields.clubName?.trim() },
        { key: 'country', label: 'Land', done: !!fields.clubCountry },
        { key: 'city', label: 'By', done: !!fields.clubCity?.trim() },
        { key: 'postalCode', label: 'Postnummer', done: !!fields.clubPostalCode?.trim() },
        { key: 'email', label: 'Email', done: !!fields.email?.trim() && fields.email.includes('@') },
        { key: 'password', label: 'Adgangskode', done: (fields.password?.length || 0) >= 6 },
      ];
      const done = checks.filter(c => c.done).length;
      return { percent: Math.round((done / checks.length) * 100), checks };
    }
    // user type
    const checks = [
      { key: 'name', label: 'Navn', done: !!fields.name?.trim() },
      { key: 'nickname', label: 'Kaldenavn', done: !!fields.nickname?.trim() },
      { key: 'email', label: 'Email', done: !!fields.email?.trim() && fields.email.includes('@') },
      { key: 'password', label: 'Adgangskode', done: (fields.password?.length || 0) >= 6 },
    ];
    const done = checks.filter(c => c.done).length;
    return { percent: Math.round((done / checks.length) * 100), checks };
  }, [userType, fields]);
};

const RegistrationProgress = ({ percent, checks }) => {
  const { t } = useLanguage();
  const barColor = percent === 100 ? 'bg-emerald-400' : percent >= 60 ? 'bg-amber-400' : 'bg-red-400';
  const textColor = percent === 100 ? 'text-emerald-400' : percent >= 60 ? 'text-amber-400' : 'text-red-400';
  const missing = checks.filter(c => !c.done);
  const importantMissing = missing.filter(c => c.important);

  return (
    <div className="mb-5" data-testid="registration-progress">
      <div className="flex items-center justify-between mb-1.5">
        <span className={`text-lg font-bold ${textColor}`} data-testid="reg-progress-percent">{percent}%</span>
        <span className="text-xs text-white/40">
          {percent === 100
            ? t('register.readyToCreate')
            : missing.length === 1
              ? t('register.singleFieldRemaining')
              : t('register.multipleFieldsRemaining').replace('{count}', missing.length)
          }
        </span>
      </div>
      <div className="h-2 bg-zinc-700 rounded-full overflow-hidden">
        <div className={`h-full ${barColor} rounded-full transition-all duration-500 ease-out`} style={{ width: `${percent}%` }} />
      </div>
      {importantMissing.length > 0 && (
        <div className="mt-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
          <p className="text-xs text-amber-300" style={{ textTransform: 'none' }}>
            <Clock className="w-3 h-3 inline mr-1" /> {t('gen.f3fc246951')}
          </p>
        </div>
      )}
    </div>
  );
};

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const { t } = useLanguage();
  
  const [name, setName] = useState('');
  const [nickname, setNickname] = useState('');
  const [clubName, setClubName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [userType, setUserType] = useState('user');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [companyName, setCompanyName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [offerDescription, setOfferDescription] = useState('');
  const [offerSubmitted, setOfferSubmitted] = useState(false);
  const [clubCreated, setClubCreated] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [hasClubhouse, setHasClubhouse] = useState(true);
  const [clubCity, setClubCity] = useState('');
  const [clubAddress, setClubAddress] = useState('');
  const [clubCountry, setClubCountry] = useState('');
  const [userCountry, setUserCountry] = useState('DK');
  const [clubPostalCode, setClubPostalCode] = useState('');
  const [clubPhone, setClubPhone] = useState('');
  const [clubDescription, setClubDescription] = useState('');
  const [clubFacilities, setClubFacilities] = useState([]);
  const [clubAltHours, setClubAltHours] = useState([]);
  const [clubSpecialDates, setClubSpecialDates] = useState([]);
  const [openingHours, setOpeningHours] = useState({
    monday: '', tuesday: '', wednesday: '',
    thursday: '', friday: '', saturday: '', sunday: ''
  });

  const progress = useRegistrationProgress(userType, {
    name, nickname, clubName, email, password, clubCity, clubAddress,
    clubCountry, clubPostalCode, openingHours,
    companyName, contactPerson: contactPerson || name, phone, offerDescription,
  });

  useEffect(() => {
    const type = searchParams.get('type');
    if (type === 'user' || type === 'club' || type === 'business') {
      setUserType(type === 'business' ? 'offer' : type);
    }
  }, [searchParams]);

  // Skjul type-vælgeren hvis type er pre-specificeret via URL (?type=user / ?type=club)
  const typeFromUrl = searchParams.get('type');
  const hideTypeSelector = !!(typeFromUrl === 'user' || typeFromUrl === 'club' || typeFromUrl === 'business');

  // Read referral code from URL (?ref=xxx)
  const referralCodeFromUrl = searchParams.get('ref') || '';

  useEffect(() => {
    let strength = 0;
    if (password.length >= 6) strength++;
    if (password.length >= 10) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    setPasswordStrength(Math.min(strength, 4));
  }, [password]);

  const passwordsMatch = password && confirmPassword && password === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (userType === 'offer') {
      if (!companyName || !contactPerson || !email || !phone || !offerDescription || !password) {
        toast.error(t('register.fillAllRequired'));
        return;
      }
      if (password !== confirmPassword) {
        toast.error(t('auth.passwordsNoMatch'));
        return;
      }
      setLoading(true);
      try {
        await register(email, password, contactPerson, 'company', '', '', 'free', {
          company_name: companyName, phone, website: website || null, offer_description: offerDescription
        }, {}, referralCodeFromUrl);
        setOfferSubmitted(true);
        toast.success(t('register.welcomeCompany'));
      } catch (error) {
        toast.error(t(error.response?.data?.detail) || error.response?.data?.detail || t('auth.errorOccurred'));
      } finally {
        setLoading(false);
      }
      return;
    }

    if (password !== confirmPassword) { toast.error(t('auth.passwordsNoMatch')); return; }
    if (password.length < 6) { toast.error(t('auth.passwordTooShortMin')); return; }
    if (userType === 'club' && !clubCity.trim()) { toast.error(t('gen.040fe261cd')); return; }
    if (userType === 'club' && !clubCountry) { toast.error(t('gen.9a79123c8e')); return; }
    if (userType === 'club' && !clubPostalCode.trim()) { toast.error(t('gen.c5fa30a2c5')); return; }
    if (userType === 'user' && !userCountry) { toast.error(t('auth.selectCountry')); return; }

    setLoading(true);
    try {
      const clubFields = userType === 'club' ? {
        no_clubhouse: !hasClubhouse,
        opening_hours: hasClubhouse ? openingHours : null,
        city: clubCity.trim(),
        address: clubAddress.trim(),
        country: clubCountry,
        postal_code: clubPostalCode.trim(),
        phone: clubPhone.trim() || undefined,
        description: clubDescription.trim() || undefined,
        facilities: clubFacilities.length > 0 ? clubFacilities : undefined,
        alternative_opening_hours: hasClubhouse && clubAltHours.length > 0 ? clubAltHours : undefined,
        special_opening_dates: hasClubhouse && clubSpecialDates.length > 0 ? clubSpecialDates : undefined,
      } : {};
      const user = await register(email, password, name, userType, nickname, clubName, 'fuld_gas', { country: userCountry }, clubFields, referralCodeFromUrl);
      if (userType === 'club') {
        setClubCreated(true);
      } else {
        toast.success(`${t('common.welcome')}, ${user.nickname || user.club_name || user.name}!`);
        navigate('/');
      }
    } catch (error) {
      const backendDetail = error.response?.data?.detail || '';
      toast.error(t(backendDetail) || backendDetail || t('register.registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const accountTypes = [
    { id: 'user', icon: User, title: t('register.user'), description: t('register.userDesc') },
    { id: 'club', icon: Building2, title: t('register.club'), description: t('register.clubDesc') },
    { id: 'offer', icon: Store, title: t('register.companyTitle'), description: t('register.companyDesc') }
  ];

  if (offerSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="glass-card text-center max-w-md w-full p-8">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 bg-emerald-500/20">
            <Check className="w-10 h-10 text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold mb-3 text-white">{t('register.welcomeToBareMC')}</h2>
          <p className="mb-4 text-zinc-300">{t('register.companyAccountCreated')}</p>
          <p className="text-sm mb-8 text-zinc-400">{t('register.companyAccountReview')}</p>
          <button 
            onClick={() => navigate('/')}
            className="w-full bg-emerald-400 text-zinc-900 font-bold py-3 rounded-xl hover:bg-emerald-300 transition-all"
            data-testid="company-success-continue"
          >
            {t('register.goToFrontPage')}
          </button>
        </div>
      </div>
    );
  }

  if (clubCreated) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" data-testid="club-created-welcome">
        <div className="glass-card max-w-lg w-full p-8">
          <div className="text-center mb-6">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 bg-emerald-500/20">
              <Check className="w-10 h-10 text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">{t('gen.599c9566b5')}</h2>
            <p className="text-zinc-300">{t('gen.92bd12e236')}</p>
          </div>

          <div className="space-y-2 mb-6">
            <p className="text-sm font-semibold text-white/70 mb-3">{t('register.dashboardIntro')}</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { icon: Camera, label: t('register.logoAndCover') },
                { icon: Clock, label: t('register.openingHours') },
                { icon: RotateCcw, label: t('register.altOpeningHoursShort') },
                { icon: Home, label: t('register.facilitiesShort') },
                { icon: Globe, label: t('register.socialMediaShort') },
                { icon: Mail, label: t('register.contactInfoShort') },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2 p-2.5 rounded-lg bg-white/5 border border-white/5">
                  <item.icon className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <span className="text-xs text-zinc-300">{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 mb-6">
            <p className="text-xs text-amber-300 text-center leading-relaxed">
              <Clock className="w-3.5 h-3.5 inline mr-1" />
              <strong>Tip:</strong> {t('register.tipOpeningHours')}
            </p>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-emerald-400 text-zinc-900 font-bold py-3 rounded-xl hover:bg-emerald-300 transition-all"
              data-testid="club-go-to-dashboard"
            >
              {t('gen.bb7f8bd702')}
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full bg-white/5 text-zinc-400 font-medium py-3 rounded-xl hover:bg-white/10 transition-all text-sm"
              data-testid="club-go-to-frontpage"
            >
              {t('register.doItLater')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Back Button */}
      <Link 
        to="/login" 
        className="absolute top-4 left-4 sm:top-6 sm:left-6 flex items-center gap-2 text-white/80 hover:text-white transition-colors z-10 py-2.5 px-4 rounded-xl hover:bg-white/10"
        data-testid="back-to-login"
      >
        <ArrowLeft className="w-5 h-5" />
        <span className="font-semibold">{t('common.back')}</span>
      </Link>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4 py-20">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center justify-center mb-5">
            <img loading="lazy" src="/assets/baremc-logo-transparent.png" alt="BareMC" className="h-16 w-auto drop-shadow-2xl" />
          </div>

          {/* Register Card */}
          <div className="glass-card p-6 sm:p-8">
            <h1 className="text-2xl sm:text-3xl text-center mb-1 text-white tracking-wide">
              {t('register.createAccountTitle')}
            </h1>
            <p className="text-center mb-6 text-white/55 text-sm">
              {t('register.joinCommunity')}
            </p>

            {/* Accent line */}
            <div className="mc-accent-line mx-auto mb-6" />

            {/* Referral banner */}
            {referralCodeFromUrl && (
              <div className="mb-5 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-center" data-testid="referral-banner">
                <p className="text-sm text-emerald-300" style={{textTransform:'none'}}>
                  <Zap className="w-4 h-4 inline mr-1" /> {t('gen.9c86963171')}
                </p>
              </div>
            )}

            {/* Account Type Selection — skjult når type er pre-valgt via URL */}
            {!hideTypeSelector && (
            <div className="mb-6">
              <label className="font-semibold mb-3 block text-white/80 text-sm normal-case" style={{textTransform:'none'}}>
                {t('register.selectAccountType')}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {accountTypes.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setUserType(type.id)}
                    className={`p-3 sm:p-4 rounded-xl border-2 transition-all duration-200 text-center ${
                      userType === type.id 
                        ? 'border-emerald-400 bg-emerald-400/10 shadow-lg shadow-emerald-400/10' 
                        : 'border-white/10 bg-white/5 hover:border-white/20'
                    }`}
                    data-testid={`type-${type.id}`}
                  >
                    <type.icon 
                      className="w-7 h-7 mx-auto mb-1.5" 
                      style={{ color: userType === type.id ? '#6ee7b7' : 'rgba(255,255,255,0.4)' }} 
                    />
                    <span 
                      className="font-bold text-xs block"
                      style={{ color: userType === type.id ? '#6ee7b7' : 'rgba(255,255,255,0.5)', textTransform: 'none' }}
                    >
                      {type.title}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Live Progress Bar */}
              <RegistrationProgress percent={progress.percent} checks={progress.checks} />
              {userType !== 'offer' && (
                <>
                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-white/80 mb-1.5" style={{textTransform:'none'}}>
                      <UserCircle className="w-4 h-4 text-emerald-400/70" />
                      {userType === 'club' ? t('register.contactPersonClub') : t('register.fullName')} *
                    </label>
                    <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                      placeholder="John Jensen" required className="input-mc" data-testid="name-input" />
                  </div>

                  {userType === 'user' && (
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-white/80 mb-1.5" style={{textTransform:'none'}}>
                        <User className="w-4 h-4 text-emerald-400/70" />
                        {t('register.nickname')}
                      </label>
                      <input type="text" value={nickname} onChange={(e) => setNickname(e.target.value)}
                        placeholder="JohnnyBiker" className="input-mc" data-testid="nickname-input" />
                    </div>
                  )}

                  {/* Country picker — required for personal user registration */}
                  {userType === 'user' && (
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-white/80 mb-1.5" style={{textTransform:'none'}}>
                        <Globe className="w-4 h-4 text-emerald-400/70" />
                        {t('register.country')} *
                      </label>
                      <div className="relative">
                        <select
                          value={userCountry}
                          onChange={(e) => setUserCountry(e.target.value)}
                          className="input-mc w-full appearance-none cursor-pointer pr-10"
                          required
                          data-testid="user-country-select"
                        >
                          <option value="">{t('register.selectCountry')}...</option>
                          {EUROPEAN_COUNTRIES.map(c => (
                            <option key={c.code} value={c.code}>
                              {countryToFlag(c.code)} {c.name}
                            </option>
                          ))}
                        </select>
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none text-[10px]">▼</span>
                      </div>
                    </div>
                  )}

                  {userType === 'club' && (
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-white/80 mb-1.5" style={{textTransform:'none'}}>
                        <Building2 className="w-4 h-4 text-emerald-400/70" />
                        {t('register.clubName')} *
                      </label>
                      <input type="text" value={clubName} onChange={(e) => setClubName(e.target.value)}
                        placeholder="Vikings MC" required className="input-mc" data-testid="club-name-input" />
                    </div>
                  )}

                  {/* Club location - universal address form */}
                  {userType === 'club' && (
                    <AddressForm
                      country={clubCountry}
                      postalCode={clubPostalCode}
                      city={clubCity}
                      address={clubAddress}
                      onChange={({ country, postal_code, city, address }) => {
                        setClubCountry(country);
                        setClubPostalCode(postal_code);
                        setClubCity(city);
                        setClubAddress(address);
                      }}
                      showCountry={true}
                      required={true}
                      testIdPrefix="club"
                    />
                  )}

                  {/* Clubhouse Toggle - Only for club registration */}
                  {userType === 'club' && (
                    <div className="space-y-3">
                      <button
                        type="button"
                        onClick={() => setHasClubhouse(!hasClubhouse)}
                        className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-300 ${
                          hasClubhouse
                            ? 'border-emerald-400 bg-emerald-400/10'
                            : 'border-white/10 bg-white/5'
                        }`}
                        data-testid="clubhouse-toggle"
                      >
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                          hasClubhouse ? 'bg-emerald-400/20' : 'bg-white/10'
                        }`}>
                          <Home className="w-6 h-6" style={{ color: hasClubhouse ? '#6ee7b7' : 'rgba(255,255,255,0.3)' }} />
                        </div>
                        <div className="flex-1 text-left">
                          <span className="font-bold text-sm block" style={{ color: hasClubhouse ? '#6ee7b7' : 'rgba(255,255,255,0.5)', textTransform: 'none' }}>
                            {t('register.hasClubhouse')}
                          </span>
                          <span className="text-xs text-white/40 block mt-0.5" style={{textTransform: 'none'}}>
                            {hasClubhouse ? t('register.clubhouseYes') : t('register.clubhouseNo')}
                          </span>
                        </div>
                        <div className={`w-12 h-7 rounded-full p-1 transition-all duration-300 ${
                          hasClubhouse ? 'bg-emerald-400' : 'bg-white/20'
                        }`}>
                          <div className={`w-5 h-5 rounded-full bg-white shadow-md transition-transform duration-300 ${
                            hasClubhouse ? 'translate-x-5' : 'translate-x-0'
                          }`} />
                        </div>
                      </button>

                      {/* Opening Hours - Only visible when has clubhouse */}
                      {hasClubhouse && (
                        <div className={`rounded-xl border p-4 space-y-2 animate-in fade-in slide-in-from-top-2 duration-300 ${
                          openingHours && Object.values(openingHours).some(v => v?.trim())
                            ? 'border-emerald-400/30 bg-emerald-400/5'
                            : 'border-amber-400/40 bg-amber-500/10'
                        }`} data-testid="opening-hours-section">
                          <div className="flex items-center gap-2 mb-3">
                            <Clock className="w-4 h-4 text-amber-400" />
                            <span className="text-sm font-semibold text-white/80" style={{textTransform: 'none'}}>{t('register.openingHours')}</span>
                            {!(openingHours && Object.values(openingHours).some(v => v?.trim())) && (
                              <span className="text-xs text-amber-400 ml-auto font-semibold" style={{textTransform: 'none'}}>{t('register.importantForVisibility')}</span>
                            )}
                            {openingHours && Object.values(openingHours).some(v => v?.trim()) && (
                              <CheckCircle2 className="w-4 h-4 text-emerald-400 ml-auto" />
                            )}
                          </div>
                          <OpeningHoursEditor
                            openingHours={openingHours}
                            onChange={(newHours) => setOpeningHours(newHours)}
                          />
                        </div>
                      )}

                      {/* Alternative åbningstider - Only when has clubhouse */}
                      {hasClubhouse && (
                        <AlternativeOpeningEditor
                          entries={clubAltHours}
                          onChange={setClubAltHours}
                        />
                      )}

                      {/* Specielle åbningsdatoer - Only when has clubhouse */}
                      {hasClubhouse && (
                        <SpecialOpeningEditor
                          specialDates={clubSpecialDates}
                          onChange={setClubSpecialDates}
                        />
                      )}
                    </div>
                  )}

                  {/* Telefon & Beskrivelse (valgfrit) */}
                  {userType === 'club' && (
                    <div className="space-y-3">
                      <div>
                        <label className="flex items-center gap-2 text-sm font-semibold text-white/80 mb-1.5" style={{textTransform:'none'}}>
                          <Phone className="w-4 h-4 text-emerald-400/70" />
                          {t('clubs.phone')}
                        </label>
                        <input type="tel" value={clubPhone} onChange={(e) => setClubPhone(e.target.value)}
                          placeholder={t('gen.0a3d79b7ea')} className="input-mc" data-testid="club-phone-input" />
                      </div>
                      <div>
                        <label className="flex items-center gap-2 text-sm font-semibold text-white/80 mb-1.5" style={{textTransform:'none'}}>
                          <FileText className="w-4 h-4 text-emerald-400/70" /> {t('gen.4ecf48a9c0')}
                        </label>
                        <textarea value={clubDescription} onChange={(e) => setClubDescription(e.target.value)}
                          placeholder={t('gen.3981307a8c')}
                          rows={3} className="input-mc resize-none" data-testid="club-description-input" />
                      </div>
                    </div>
                  )}

                  {/* Faciliteter (valgfrit) */}
                  {userType === 'club' && (
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 text-sm font-semibold text-white/80" style={{textTransform:'none'}}>
                        {t('register.facilitiesOptional')}
                      </label>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {FACILITIES_LIST.map(facility => {
                          const isSelected = clubFacilities.includes(facility.id);
                          return (
                            <button
                              key={facility.id}
                              type="button"
                              onClick={() => {
                                setClubFacilities(prev => isSelected
                                  ? prev.filter(f => f !== facility.id)
                                  : [...prev, facility.id]
                                );
                              }}
                              className={`p-2 rounded-lg border text-center transition-all ${
                                isSelected
                                  ? 'border-emerald-400/50 bg-emerald-400/10'
                                  : 'border-white/10 bg-white/5 hover:border-white/20'
                              }`}
                              data-testid={`facility-${facility.id}`}
                            >
                              <FacilityIcon facilityId={facility.id} size={18} className={`mx-auto ${isSelected ? 'text-emerald-400' : 'text-zinc-500'}`} />
                              <span className="text-[10px] block mt-1 text-white/60" style={{textTransform:'none'}}>{t(`facilities.${facility.id}`) || facility.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-white/80 mb-1.5" style={{textTransform:'none'}}>
                      <Mail className="w-4 h-4 text-emerald-400/70" />
                      {t('auth.email')} *
                    </label>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="din@email.com" required autoComplete="email" className="input-mc" data-testid="email-input" />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-white/80 mb-1.5" style={{textTransform:'none'}}>
                      <Lock className="w-4 h-4 text-emerald-400/70" />
                      {t('auth.password')} *
                    </label>
                    <div className="relative">
                      <input type={showPassword ? 'text' : 'password'} value={password}
                        onChange={(e) => setPassword(e.target.value)} placeholder={t('auth.minSixChars')}
                        required className="input-mc pr-12" data-testid="password-input" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-white/40 hover:text-white/70">
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {password && (
                      <div className="flex gap-1 mt-2">
                        {[1, 2, 3, 4].map((level) => (
                          <div key={level} className="h-1.5 flex-1 rounded-full transition-colors"
                            style={{ background: passwordStrength >= level 
                              ? level <= 1 ? '#ef4444' : level <= 2 ? '#f59e0b' : '#22c55e' : 'rgba(255,255,255,0.1)' }} />
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-white/80 mb-1.5" style={{textTransform:'none'}}>
                      <Lock className="w-4 h-4 text-emerald-400/70" />
                      {t('auth.confirmPassword')} *
                    </label>
                    <div className="relative">
                      <input type={showPassword ? 'text' : 'password'} value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)} placeholder={t('register.repeatPassword')}
                        required className="input-mc pr-12"
                        style={{ borderColor: confirmPassword ? (passwordsMatch ? '#22c55e' : '#ef4444') : undefined }}
                        data-testid="confirm-password-input" />
                      {confirmPassword && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {passwordsMatch ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <AlertCircle className="w-5 h-5 text-red-400" />}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {userType === 'offer' && (
                <>
                  <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                    <p className="text-sm text-amber-300/90" style={{textTransform:'none'}}>{t('register.companyInfoBanner')}</p>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-white/80 mb-1.5 block" style={{textTransform:'none'}}>{t('register.companyNameLabel')} *</label>
                    <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)}
                      placeholder={t('gen.56855f94d0')} required className="input-mc" data-testid="company-name-input" />
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-white/80 mb-1.5 block" style={{textTransform:'none'}}>{t('register.contactPersonLabel')} *</label>
                    <input type="text" value={contactPerson} onChange={(e) => setContactPerson(e.target.value)}
                    placeholder={t('register.yourName')} required className="input-mc" data-testid="contact-person-input" />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-white/80 mb-1.5" style={{textTransform:'none'}}>
                      <Mail className="w-4 h-4 text-emerald-400/70" /> Email *
                    </label>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="firma@email.com" required className="input-mc" data-testid="company-email-input" />
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-white/80 mb-1.5 block" style={{textTransform:'none'}}>{t('register.phoneLabel')} *</label>
                    <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                      placeholder="12 34 56 78" required className="input-mc" data-testid="company-phone-input" />
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-white/80 mb-1.5 block" style={{textTransform:'none'}}>{t('register.websiteOptional')}</label>
                    <input type="url" value={website} onChange={(e) => setWebsite(e.target.value)}
                      placeholder="https://www.yourclub.com" className="input-mc" data-testid="company-website-input" />
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-white/80 mb-1.5 block" style={{textTransform:'none'}}>{t('register.describeOfferLabel')} *</label>
                    <textarea value={offerDescription} onChange={(e) => setOfferDescription(e.target.value)}
                      placeholder={t('register.describeOfferPlaceholder')} required rows={3}
                      className="input-mc resize-none" style={{ minHeight: '100px' }} data-testid="company-description-input" />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-white/80 mb-1.5" style={{textTransform:'none'}}>
                      <Lock className="w-4 h-4 text-emerald-400/70" /> {t('auth.password')} *
                    </label>
                    <div className="relative">
                      <input type={showPassword ? 'text' : 'password'} value={password}
                        onChange={(e) => setPassword(e.target.value)} placeholder={t('auth.minSixChars')}
                        required className="input-mc pr-12" data-testid="company-password-input" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-white/40 hover:text-white/70">
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-white/80 mb-1.5" style={{textTransform:'none'}}>
                      <Lock className="w-4 h-4 text-emerald-400/70" /> {t('auth.confirmPassword')} *
                    </label>
                    <div className="relative">
                      <input type={showPassword ? 'text' : 'password'} value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)} placeholder={t('register.repeatPassword')}
                        required className="input-mc pr-12"
                        style={{ borderColor: confirmPassword ? (passwordsMatch ? '#22c55e' : '#ef4444') : undefined }}
                        data-testid="company-confirm-password-input" />
                      {confirmPassword && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {passwordsMatch ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <AlertCircle className="w-5 h-5 text-red-400" />}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Submit Button */}
              <button type="submit" disabled={loading}
                className="w-full bg-emerald-400 text-zinc-900 font-bold text-base py-3.5 rounded-xl hover:bg-emerald-300 transition-all shadow-lg shadow-emerald-400/20 disabled:opacity-50 mt-2"
                style={{textTransform:'none'}}
                data-testid="register-submit"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-zinc-900/30 border-t-zinc-900 rounded-full animate-spin" />
                    {userType === 'offer' ? t('register.sendingApplication') : t('register.creatingAccount')}
                  </span>
                ) : (
                  userType === 'offer' ? t('register.sendApplication') : t('register.createAccount')
                )}
              </button>
            </form>

            <div className="mt-5 text-center">
              <p className="text-white/50 text-sm" style={{textTransform:'none'}}>
                {t('register.alreadyHaveAccount')}{' '}
                <Link to="/login" className="font-bold text-emerald-400 hover:text-emerald-300 hover:underline">
                  {t('auth.login')}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
